import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {Base64} from 'js-base64';

import BaseComponent from 'common/BaseComponent';
import Table from 'common/Table';
import {ValidationResult} from 'validation'
import readIcon from './readItem.png';
import editIcon from './editItem.png';
import deleteIcon from './deleteItem.png';
import deleteNotAllowedIcon from './actionNotAllowed.png';
import notSentIcon from './envelope.png';
import sendIcon from './sendIcon.png';
import deliveredIcon from './success.png';
import failedIcon from './failed.png';
import theme from './DocumentTab.scss';
import conf from 'configuration.json';
import {putAll} from 'utils/Array';
import update from 'immutability-helper';
import Select from 'common/Select';
import {getMessage as msg} from 'MessageBundle';
import {replacePlaceholders} from 'common/Replace';
import GenerateDocumentPopup from './GenerateDocumentPopup';
import EmailPopup from 'common/EmailPopup';
import {prepareFileName} from 'utils/FileName';

const MAX_FILE_SIZE = 10*1024*1024;
const ALWAYS_ALLOWED_EMAIL_TYPE = "Beleg-Anforderung";
const NOT_SENT = "NOT_SENT";
const DELIVERED = "DELIVERED";
const FAILED = "FAILED";
const SYSTEM_AUTHOR_NAME = 'SYSTEM';

const SEND_ICONS = new Map([
    [NOT_SENT, {icon: deliveredIcon, text: msg('documentTab.send.icon.send')}],
    [DELIVERED, {icon: notSentIcon, text: msg('documentTab.send.icon.success')}],
    [FAILED, {icon: failedIcon, text: msg('documentTab.send.icon.failed')}]
]);

const OFFICE_DOCUMENT_TYPES = ['docx', 'DOCX', 'doc', 'DOC', 'xlsx', 'XLSX', 'xls', 'XLS'];

export default class DocumentTab extends BaseComponent {

    constructor(props) {
        super(props);

        this.state = {
            documentTypes: [],
            generateDocumentPopupShown: false,
            activeEmail: undefined
        };

        this.tableStructure = [
            {
                // Document Action
                label: msg('documentTab.column.action'), fullLabel: msg("documentTab.column.action.full"),
                width: '75px', bodyStyle: theme.columnAction,
                render: (row, index) => {
                    return (
                        <div>
                            <a href="javascript:void(0)" className={theme.readIcoLnk} onClick={() => this.viewDocument(index)}
                                title={msg('documentTab.viewItem') + ' ' + prepareFileName(row.name)}>
                                <img src={readIcon} alt=""/>
                            </a>
                            {row.editable && this.props.editableAreas.indexOf('DOCUMENTS_TABLE') >= 0 ?
                                <a href="javascript:void(0)" className={theme.editIcoLnk} onClick={() => this.editAttachment(index)}
                                    title={msg('documentTab.editItem') + ' ' +prepareFileName(row.name)}>
                                    <img src={editIcon} alt=""/>
                                </a> : null
                            }
                            {this.renderSendIcon(index)}
                        </div>
                    )
                }
            },
            {
                // File Name
                label: msg('documentTab.column.fileName'), width: '105px',
                render: (row) => {
                    return <div className={theme.txtOverflow} title={row.displayableName}>{row.displayableName}</div>;
                }
            },
            {
                // Document Type
                label: msg('documentTab.column.documentType'), width: '135px', bodyStyle: theme.documentTypeChoice,
                render: (row, index, errorMessages) => {
                    return (
                        <Select id={"type-select"+index} value={row.type || ""} options={new Map(this.state.documentTypes.map(t => [t, t]))}
                            emptyValue={msg("documentTab.documentTypeChoice")} name="documentType" index={index} errorMessages={errorMessages}
                            onChange={this.handleTypeSelect(index)}
                            disabled={this.props.editableAreas.indexOf('DOCUMENTS_TABLE') === -1 && !this.isAdminEditAllowed(row.isNew)}
                            showExclamationMark={false}/>
                    );
                }
            },
            {
                // Author
                label: msg('documentTab.column.author'), width: '105px',
                render: (row) => {
                    const author = row.author === SYSTEM_AUTHOR_NAME ? '' : row.author;
                    return <div className={theme.txtOverflow} title={author}>{author}</div>;
                }
            },
            {
                // Updated
                label: msg('documentTab.column.updated'), width: '110px', bodyStyle: theme.txtRight,
                render: (row) => {
                    return moment(row.updated).format('DD.MM.YY, HH:mm');
                }
            },
            {
                // Viewed by user
                label: msg('documentTab.column.viewed'), fullLabel: msg("documentTab.column.viewed.full"),
                width: '45px', headStyle: theme.columnViewedUser,
                render: (row) => {
                    return row.viewers.includes(this.props.editor) ? "ja" : "nein";
                }
            },
            {
                // Mail Delivery State
                label: msg('documentTab.column.mailDeliveryState'), fullLabel: msg("documentTab.column.mailDeliveryState.full"),
                width: '60px', headStyle: theme.columnKomm, bodyStyle: theme.txtCenter,
                render: (row) => {
                    if(row.senderEmail) {
                        const sendIcon = SEND_ICONS.get(row.deliveryStatus);
                        const lastDeliveryDate = moment(row.lastDeliveryDate).format('DD.MM.YY, HH:mm');
                        const hint = replacePlaceholders(sendIcon.text, lastDeliveryDate);
                        return (
                            <img src={sendIcon.icon} alt={hint} className={theme.deliveryStatus} title={hint}/>
                        );
                    }
                }
            },
            {
                // Delete Action
                label: msg('documentTab.column.delete'), width: '60px', headStyle: theme.columnDeleteDocument, bodyStyle: theme.txtCenter,
                render: (row, index) => {
                    return this.isDeletable(row) ?
                        <a href="javascript:void(0)" onClick={this.remove(index)} title={msg("documentTab.deleteDocument")}>
                            <img src={deleteIcon} alt=""/>
                        </a> :
                        <img src={deleteNotAllowedIcon} className={theme.notAllowedIcoLnk} alt={msg('documentTab.deletionNotAllowedTitle')}
                            title={msg('documentTab.deletionNotAllowedTitle')}/>;
                }
            }
        ];
    }

    /**
     * The function is necessary to allow user add duplicate file and add files that were removed using delete button.
     */
    addFileEvent = function () {
        this.value = "";
    };

    isDeletable = (row) => {
        const deletable = row.deletable && this.props.editableAreas.indexOf('DOCUMENTS_TABLE') >= 0;
        const independentDeletable = row.independentDeletable;
        const deletableByAdmin = this.isAdminEditAllowed(row.isNew);
        return deletable || independentDeletable || deletableByAdmin;
    };

    isAdminEditAllowed = (newAttachment) => {
        return this.props.adminEditable && newAttachment;
    };

    renderSendIcon = (index) => {
        const attachment = this.props.value.attachments[index];
        const emailSendingAllowed = this.props.complaint.emailSendingAllowed;
        const deliverable = attachment.senderEmail;
        if(deliverable && (emailSendingAllowed || ALWAYS_ALLOWED_EMAIL_TYPE === attachment.type)) {
            return (
                <a href="javascript:void(0)" title={msg('documentTab.send.icon.send')} onClick={this.sendDocument(index)}>
                    <img src={sendIcon} alt=""/>
                </a>
            );
        }
    };

    handleAttachmentDeliveryResult = (result, index) => {
        const deliveryStatus = result.body.status;
        const notDelivered = deliveryStatus !== DELIVERED;
        const updated = update(this.props.value.attachments, {[index]: {
            deliveryStatus:
                {$set: deliveryStatus},
            editable:
                {$set: notDelivered},
            deletable:
                {$set: notDelivered}
        }});

        const validationResult = new ValidationResult(this.props.errorMessages);
        validationResult.resetGroups(["sendAttachment"]);

        if(deliveryStatus === DELIVERED) {
            updated[index].lastDeliveryDate = result.body.date;
        } else {
            const validationResult = new ValidationResult(this.props.errorMessages);
            validationResult.setError("sendAttachment", msg("documentTab.send.error"));
            this.props.onError(validationResult.getErrorMessages());
        }

        this.props.onChange({'attachments': updated});
        this.props.onEmailSend(result.body.comment);
        this.props.onProgressComplete();
    };

    sendDocument = (index) => () => {
        this.props.onProgressStart();
        const params = {
            attachment: this.props.value.attachments[index],
            email: this.props.complaint.email,
            complaintNumber: this.props.complaint.complaintNumber,
            fileToken: this.props.value.fileToken
        };

        this.request('post', conf.rest.sendAttachmentEndpoint).params(params).send((result) => {
            this.handleAttachmentDeliveryResult(result, index);
        }, () => {
            this.handleAttachmentDeliveryResult(FAILED, index);
        });
    };

    componentDidMount() {
        this.input.addEventListener('click', this.addFileEvent, false);

        this.props.onProgressStart();
        this.request('get', conf.rest.documentTypeEndpoint).send((result) => {
            this.setState({
                documentTypes: result.body,
            }, this.props.onProgressComplete());
        });
    }

    componentWillUnmount() {
        this.input.removeEventListener('click', this.addFileEvent, false);
    }

    updateAttachmentViewers = (index) => {
        let viewers = this.props.value.attachments[index].viewers;
        let editor = this.props.complaint.editor.accountName;
        if (!viewers.includes(editor)) {
            let updatedAttachments = update(this.props.value.attachments, {[index]: {viewers: {$push: [editor]}}});
            this.props.onChange({'attachments': updatedAttachments});
        }
    };


    showEmailDocument = (name, subject, readonly, index) => {
        this.updateAttachmentViewers(index);
        const queryUrl = conf.rest.attachmentEndpoint + '/' + this.props.complaint.complaintNumber + '/' + this.props.value.fileToken + '/' + encodeURIComponent(name);
        this.request('get', queryUrl).send((result) => {
            const content = Base64.decode(result.body.file);
            this.setState({
                activeEmail: {
                    name: name,
                    subject: subject,
                    content: content,
                    readonly: readonly,
                    index : index
                },
                activeFocus: document.activeElement
            });
        });
    };

    handleEmailClose = () => {
        this.state.activeFocus.focus();
        this.setState({
            activeEmail: undefined,
            activeFocus: undefined
        });
    };


    handleEmailSave = (subject, content) => {
        const params = {
            content: content,
            displayableName: this.state.activeEmail.name,
            complaintNumber: this.props.complaint.complaintNumber,
            fileToken: this.props.value.fileToken
        };
        this.request('post', conf.rest.saveEmailEndpoint).params(params).send(() => {
            const updated = update(this.props.value.attachments, {[this.state.activeEmail.index]: {
                subject: {$set: subject}
            }});
            this.props.onChange({'attachments': updated});
            this.handleEmailClose();
        });
    };

    viewDocument = (index) => {
        let row = this.props.value.attachments[index];
        if (row.senderEmail) {
            this.showEmailDocument(row.name, row.subject, true, index);
        } else {
            this.props.onDownload(index);
        }
    };

    editAttachment = (index) => {
        let row = this.props.value.attachments[index];
        if (row.senderEmail) {
            this.showEmailDocument(row.name, row.subject, false, index);
        } else {
            this.editDocument(index);
        }
    };

    editDocument = (index) => {
        this.props.removeCloseConfirmation();
        let fileName = this.props.value.attachments[index].name;
        const editUrl = conf.rest.attachmentEndpoint + '/' + this.props.complaint.complaintNumber + '/' + this.props.value.fileToken + '/' + encodeURIComponent(fileName);
        this.request('put', editUrl).send((result) => {
            this.handleChange(index, {
                updated: moment(),
                author: result.body.editor,
                viewers: [result.body.editor]
            });
            if(this.isOfficeDocument(fileName)) {
                window.location.href = result.body.url;
            } else {
                window.open(result.body.url);
            }
            setTimeout(this.props.addCloseConfirmation, 1000);
        });
    };

    isOfficeDocument(fileName) {
        return OFFICE_DOCUMENT_TYPES.includes(fileName.split('.').pop());
    };

    selectFile = (e) => {
        e.preventDefault();
        this.input.click(e);
    };

    handleFileSelect = (e) => {
        this.props.onProgressStart();
        let documents = Array.from(e.target.files);
        if (this.checkFiles(documents)) {
            let formData = new FormData();
            formData.append("complaintNumber", this.props.complaint.complaintNumber);
            formData.append("fileToken", this.props.value.fileToken);
            this.uploadAttachments(documents, formData, "attachments", this.performDocumentsUpload);
        } else {
            this.props.onProgressComplete();
        }
    };

    handleTypeSelect = (index) => (event) => {
        this.handleChange(index, {
            type: event.target.value
        });
    };

    handleChange = (index, changes) => {
        const attachmentChanged = Object.assign({}, this.props.value.attachments[index], changes);
        let updatedAttachments = update(this.props.value.attachments, {$splice: [[index, 1, attachmentChanged]]});
        this.props.onChange({'attachments': updatedAttachments});
    };

    remove = (index) => () => {
        const displayableName = this.props.value.attachments[index].displayableName;
        if(confirm(replacePlaceholders(msg("documentTab.deleteConfirm"), displayableName))) {
            this.props.onRemove(replacePlaceholders(msg("documentTab.fileRemoveComment"), displayableName));
            const removedAttachment = this.props.value.attachments[index];
            const updated = update(this.props.value.attachments, {$splice: [[index, 1]]});
            this.props.onChange({'attachments': updated}, {'deletedAttachments': [removedAttachment]});
            const validationResult = new ValidationResult(this.props.errorMessages);
            validationResult.resetByIndex("documentTab", index);
            this.props.onError(validationResult.getErrorMessages());
        }
    };

    checkFiles = (files) => {
        let checked = true;
        const validationResult = new ValidationResult(this.props.errorMessages);
        validationResult.resetGroups(["documentAddition"]);
        files.some((file) => {
            if (file.size > MAX_FILE_SIZE && checked) {
                validationResult.setError("documentAddition", msg('editComplaint.documentTab.validation.fileSizeError'));
                checked = false;
                return true;
            } else {
                return false;
            }
        });
        this.props.onError(validationResult.getErrorMessages());
        return checked;
    };

    performDocumentsUpload = (formData) => {
        this.request('post', conf.rest.attachmentEndpoint).params(formData).send((result) => {
            const attachments = result.body;
            attachments.forEach((a) => { a.isNew = true });
            let fileList = putAll(this.props.value.attachments, attachments);
            this.props.onChange({'attachments': fileList});
            this.props.onProgressComplete();
        }, (error, result) => {
            const validationResult = new ValidationResult(this.props.errorMessages);
            if (result && result.status === 412) {
                const getProcessingErrors = () => {
                    return new Map([
                        ["INVALID_ATTACHMENT_CONTENT", {
                            message: msg("common.attachmentValidationFault"),
                            invalidComponent: "attachments"
                        }],
                        ["INVALID_ATTACHMENT_SIZE", {
                            message: msg("editComplaint.documentTab.validation.fileSizeError"),
                            invalidComponent: "attachments"
                        }]
                    ]);
                };
                const errorDescriptor = getProcessingErrors().get(result.body.message);
                validationResult.setError("documentAddition", errorDescriptor.message);
            } else {
                validationResult.setError("documentAddition", msg("common.technicalError"));
            }
            this.props.onError(validationResult.getErrorMessages());
            this.props.onProgressComplete();
        });
    };

    toggleGenerateDocumentPopup = () => {
        const generateDocumentPopupShown = !this.state.generateDocumentPopupShown;
        this.setState({
            generateDocumentPopupShown: generateDocumentPopupShown
        });
        if (!generateDocumentPopupShown) {
            this.generateDocumentBtn.focus();
        }
    };

    createDocumentFromTemplate = (documentTemplateId, hvo) => {
        this.props.removeCloseConfirmation();
        this.props.onProgressStart();
        const params = {
            complaint: this.props.complaint,
            documentTemplateId: documentTemplateId,
            hvo: hvo,
            fileToken: this.props.value.fileToken
        };
        this.request('post', conf.rest.attachmentGenerateEndpoint).params(params).send((result) => {
            let generatedAttachment = result.body.attachmentOverview;
            let fileList = putAll(this.props.value.attachments, generatedAttachment);
            this.props.onChange({'attachments': fileList});
            this.props.onProgressComplete();
            if (!generatedAttachment.senderEmail) {
                window.location.href = result.body.url;
            }
            setTimeout(this.props.addCloseConfirmation, 1000);
            this.setState({
                generateDocumentPopupShown: false
            });

            this.generateDocumentBtn.focus();
        });
    };

    render() {
        return (
            <div className={theme.docTab}>
                <div className={theme.clear}>
                    <div className={theme.contentWrap}>
                        <h3>Dokumente zum Vorgang</h3>

                        <div className={theme.tblWrap}>
                            <div className={theme.tblWrapInner}>
                                <Table structure={this.tableStructure} value={this.props.value.attachments}
                                    errorMessages={this.props.errorMessages} className={theme.docTabTbl}/>
                            </div>
                        </div>

                        <input type="file" multiple="multiple" tabIndex="-1" ref={(input) => this.input = input} className={theme.hiddenInput}
                            disabled={this.props.editableAreas.indexOf('UPLOAD_BUTTON') === -1} onChange={this.handleFileSelect}/>
                        <div className={theme.btnGroupMini}>
                            <button onClick={this.selectFile} disabled={this.props.editableAreas.indexOf('UPLOAD_BUTTON') === -1}
                                className={theme.buttonGray}>Dokument anfügen</button>
                            <button onClick={this.toggleGenerateDocumentPopup} disabled={this.props.editableAreas.indexOf('CREATE_BUTTON') === -1}
                                className={theme.buttonGray} ref={(generateDocumentBtn) => this.generateDocumentBtn = generateDocumentBtn}>
                                Vorlage anfügen
                            </button>
                        </div>
                    </div>

                    {this.props.countersigningPanel()}
                </div>
                {this.state.activeEmail ?
                    <EmailPopup value={this.state.activeEmail} onClose={this.handleEmailClose} onSave={this.handleEmailSave} emailDocument/>
                    : null
                }

                {this.state.generateDocumentPopupShown ?
                    <GenerateDocumentPopup uvoAvailable={this.props.uvoAvailable}
                        onClose={this.toggleGenerateDocumentPopup} onGenerateTemplate={this.createDocumentFromTemplate} />
                : null}
            </div>
        );
    }
}

DocumentTab.propTypes = {

    /**
     * The documentTab part of complaint with all data needed for document Tab.
     */
    value: PropTypes.object.isRequired,

    /**
     * Function, called on document list change
     */
    onChange: PropTypes.func.isRequired,

    /**
     * Function, called when attachment was removed.
     */
    onRemove: PropTypes.func.isRequired,

    /**
     * Function, called in case error is happened
     */
    onError: PropTypes.func.isRequired,

    /**
     * Function, called on document download
     */
    onDownload: PropTypes.func.isRequired,

    /**
     * Function, called when uploading starts
     */
    onProgressStart: PropTypes.func.isRequired,

    /**
     * Function, called when uploading ends
     */
    onProgressComplete: PropTypes.func.isRequired,

    /**
     * The current complaint
     */
    complaint: PropTypes.object.isRequired,

    /**
     * The current operator name
     */
    editor: PropTypes.string.isRequired,

    /**
     * Renders Countersigning panel.
     */
    countersigningPanel: PropTypes.func.isRequired,

    /**
     * Error messages to be shown on the tab.
     */
    errorMessages: PropTypes.object,

    /**
     * Boolean flag indicating that UVO is available for the current complaint.
     */
    uvoAvailable: PropTypes.bool,

    /**
     * Array of areas to be enabled for the user.
     */
    editableAreas: PropTypes.array,

    /**
     * Add comment, when was email sending.
     */
    onEmailSend: PropTypes.func.isRequired,

    /**
     * Boolean flag indicating that admin can upload files.
     */
    adminEditable: PropTypes.bool
};



// WEBPACK FOOTER //
// ./src/dialogs/VPRekla/EditComplaint/DocumentTab/index.js